import { z } from "zod";
import { FONTES_DA_FOTO, enquadramentoInicial, } from "./impressao.js";
/**
 * As contas do enquadramento com IA, sem IA.
 *
 * A IA entra em dois pontos — achar as pessoas e completar o cenário —, mas as
 * decisões ficam aqui, em funções puras: de que lado se pode ampliar, onde a
 * foto cai no quadro, qual recorte mantém todo mundo inteiro. Regra se testa sem
 * modelo e sem gastar; e é aqui que mora a regra inegociável do pedido: ampliar
 * mexe só no cenário, nunca nas pessoas.
 */
// ─── O que a visão devolve ────────────────────────────────────────────────────
export const BORDAS_DA_FOTO = ["topo", "base", "esquerda", "direita"];
/** Fração da foto, de 0 a 1, com a origem no canto superior esquerdo. */
const fracao = z.number().finite();
const caixaSchema = z.object({
    x: fracao,
    y: fracao,
    largura: fracao,
    altura: fracao,
});
export const pessoaDetectadaSchema = z.object({
    corpo: caixaSchema,
    rosto: caixaSchema.optional(),
    // Valor que o modelo inventa (ex.: "fundo") não derruba a leitura: some.
    cortada: z
        .array(z.string())
        .default([])
        .transform((bordas) => bordas.filter((b) => BORDAS_DA_FOTO.includes(b))),
});
export const deteccaoDePessoasSchema = z.object({
    pessoas: z.array(pessoaDetectadaSchema).max(80),
    observacao: z.string().optional(),
});
/**
 * Lê a resposta do modelo de visão.
 *
 * Tolera o que modelos fazem — cercar o JSON com ```json, escrever uma frase
 * antes — e devolve `null` quando não há o que aproveitar, em vez de lançar: a
 * quem chama cabe dizer "não consegui achar as pessoas".
 */
export function lerDeteccaoDePessoas(texto) {
    const inicio = texto.indexOf("{");
    const fim = texto.lastIndexOf("}");
    if (inicio === -1 || fim <= inicio)
        return null;
    try {
        const bruto = JSON.parse(texto.slice(inicio, fim + 1));
        const lido = deteccaoDePessoasSchema.safeParse(bruto);
        return lido.success ? lido.data : null;
    }
    catch {
        return null;
    }
}
const limitar01 = (n) => Math.min(Math.max(n, 0), 1);
/** A caixa dentro da foto: o modelo erra por uns pontos para fora da borda. */
function dentroDaFoto(c) {
    const x0 = limitar01(c.x);
    const y0 = limitar01(c.y);
    return {
        x0,
        y0,
        x1: Math.max(x0, limitar01(c.x + c.largura)),
        y1: Math.max(y0, limitar01(c.y + c.altura)),
    };
}
// ─── Que bordas cortam alguém ─────────────────────────────────────────────────
/** Quanto da foto (1%) uma caixa pode estar longe da borda e ainda "encostar". */
const ENCOSTA = 0.01;
/**
 * As bordas da foto em que alguém é cortado.
 *
 * Une o que o modelo diz com o que as caixas mostram, por segurança: quem encosta
 * na borda de cima, da esquerda ou da direita conta como cortado mesmo que o
 * modelo não tenha dito. A **base** fica só com o modelo, porque pés rente à borda
 * de baixo são corpo inteiro, e não corpo cortado.
 *
 * Importa porque, no ensaio, o modelo de imagem **continua o corpo** de quem a
 * borda corta — é mexer em pessoa, e a ampliação não pode ir para esse lado.
 */
export function bordasCortadas(pessoas) {
    const bordas = new Set();
    for (const p of pessoas) {
        for (const b of p.cortada)
            bordas.add(b);
        const { x0, y0, x1 } = dentroDaFoto(p.corpo);
        if (y0 <= ENCOSTA)
            bordas.add("topo");
        if (x0 <= ENCOSTA)
            bordas.add("esquerda");
        if (x1 >= 1 - ENCOSTA)
            bordas.add("direita");
    }
    return bordas;
}
// ─── Ampliar a cena ───────────────────────────────────────────────────────────
/** Mais que isso é inventar cenário demais para a foto continuar sendo a foto. */
export const AMPLIACAO_MAXIMA = 0.75;
/**
 * O quadro maior em que a foto cabe na proporção da célula, e de que lado
 * crescer.
 *
 * Célula mais alta que a foto cresce em cima e embaixo; mais larga, nos lados.
 * Dos dois lados possíveis, só conta o que **não** corta ninguém; se só um está
 * livre, a foto fica encostada no outro e a faixa toda vai no livre (no ensaio,
 * pessoas cortadas na cintura continuaram cortadas assim). Se nenhum está livre,
 * recusa — a quem chama cabe oferecer "ajustar" ou "encaixar".
 */
export function planejarAmpliacao(foto, celula, cortadas) {
    const alvo = celula.largura / celula.altura;
    const atual = foto.largura / foto.altura;
    if (Math.abs(alvo - atual) / atual < 0.01)
        return { ok: false, motivo: "desnecessaria" };
    const vertical = alvo < atual; // célula mais alta que a foto
    const [ladoA, ladoB] = vertical
        ? ["topo", "base"]
        : ["esquerda", "direita"];
    const livreA = !cortadas.has(ladoA);
    const livreB = !cortadas.has(ladoB);
    const quadro = vertical
        ? { largura: foto.largura, altura: Math.round(foto.largura / alvo) }
        : { largura: Math.round(foto.altura * alvo), altura: foto.altura };
    const extra = vertical ? quadro.altura - foto.altura : quadro.largura - foto.largura;
    const base = vertical ? foto.altura : foto.largura;
    if (!livreA && !livreB)
        return { ok: false, motivo: "cortada", lados: [ladoA, ladoB] };
    if (extra / base > AMPLIACAO_MAXIMA) {
        return { ok: false, motivo: "grande", necessario: extra / base };
    }
    // Quanto vai para cada lado: metade e metade; ou tudo no livre.
    const antes = livreA && livreB ? Math.round(extra / 2) : livreA ? extra : 0;
    const depois = extra - antes;
    const novos = [antes > 0 ? ladoA : null, depois > 0 ? ladoB : null].filter((l) => l !== null);
    // Todo lado sem faixa nova: a foto fica encostada nele.
    const preservados = BORDAS_DA_FOTO.filter((b) => !novos.includes(b));
    return {
        ok: true,
        plano: {
            quadro,
            foto: vertical ? { x: 0, y: antes } : { x: antes, y: 0 },
            novos,
            preservados,
        },
    };
}
/** "em cima e embaixo", "à esquerda"…: para dizer ao modelo onde está o espaço vazio. */
export function descreverLados(lados) {
    const nome = {
        topo: "em cima",
        base: "embaixo",
        esquerda: "à esquerda",
        direita: "à direita",
    };
    if (lados.length === 2 && lados.includes("topo") && lados.includes("base")) {
        return "em cima e embaixo";
    }
    if (lados.length === 2 && lados.includes("esquerda") && lados.includes("direita")) {
        return "dos dois lados, à esquerda e à direita";
    }
    return lados.map((l) => nome[l]).join(" e ");
}
/** "na borda de baixo"…: onde a foto fica encostada, sem ganhar espaço. */
export function descreverBordas(bordas) {
    const nome = {
        topo: "de cima",
        base: "de baixo",
        esquerda: "da esquerda",
        direita: "da direita",
    };
    if (bordas.length === 0)
        return "no centro do quadro";
    return `na borda ${bordas.map((b) => nome[b]).join(" e na borda ")} do quadro`;
}
/** A folga em volta do grupo: 3% da foto de cada lado. */
const MARGEM = 0.03;
/** O espaço acima das cabeças, em fração da altura da janela. */
const FOLGA_EM_CIMA = 0.1;
const entre = (v, min, max) => Math.min(Math.max(v, min), max);
/**
 * O recorte da proporção da célula que mantém **todas** as pessoas inteiras.
 *
 * Usa a maior janela possível dentro da foto — a que "preenche" a célula —, que
 * preserva o máximo da cena, e a **desloca** até incluir o grupo com a margem,
 * centrada nele e com folga acima das cabeças. Nada é gerado: é só onde cortar.
 * Se o grupo não cabe nessa janela, devolve a menor janela que o contém (com
 * margem branca onde passar da foto) e avisa `faltaCenario`.
 */
export function ajustarAPessoas(foto, celula, pessoas) {
    const cobertura = enquadramentoInicial(foto, celula, "preencher");
    if (pessoas.length === 0) {
        return { enquadramento: cobertura, faltaCenario: false, semPessoas: true };
    }
    const proporcao = celula.largura / celula.altura;
    const janelaL = (cobertura.largura / 100) * foto.largura;
    const janelaA = janelaL / proporcao;
    // O grupo, em pixels da foto.
    let x0 = Infinity;
    let y0 = Infinity;
    let x1 = -Infinity;
    let y1 = -Infinity;
    for (const p of pessoas) {
        const c = dentroDaFoto(p.corpo);
        x0 = Math.min(x0, c.x0 * foto.largura);
        y0 = Math.min(y0, c.y0 * foto.altura);
        x1 = Math.max(x1, c.x1 * foto.largura);
        y1 = Math.max(y1, c.y1 * foto.altura);
    }
    // Com margem, mas sem passar da foto: quem encosta na borda não ganha folga fora.
    const gx0 = Math.max(0, x0 - MARGEM * foto.largura);
    const gx1 = Math.min(foto.largura, x1 + MARGEM * foto.largura);
    const gy0 = Math.max(0, y0 - MARGEM * foto.altura);
    const gy1 = Math.min(foto.altura, y1 + MARGEM * foto.altura);
    const cabe = gx1 - gx0 <= janelaL + 0.5 && gy1 - gy0 <= janelaA + 0.5;
    if (cabe) {
        const x = entre((x0 + x1) / 2 - janelaL / 2, Math.max(gx1 - janelaL, 0), Math.min(gx0, foto.largura - janelaL));
        const y = entre(y0 - FOLGA_EM_CIMA * janelaA, Math.max(gy1 - janelaA, 0), Math.min(gy0, foto.altura - janelaA));
        return {
            enquadramento: {
                x: (x / foto.largura) * 100,
                y: (y / foto.altura) * 100,
                largura: cobertura.largura,
            },
            faltaCenario: false,
            semPessoas: false,
        };
    }
    // Não cabe: a menor janela que contém o grupo (com a margem inteira), centrada.
    const w = Math.max(x1 - x0 + 2 * MARGEM * foto.largura, (y1 - y0 + 2 * MARGEM * foto.altura) * proporcao);
    const h = w / proporcao;
    return {
        enquadramento: {
            x: (((x0 + x1) / 2 - w / 2) / foto.largura) * 100,
            y: (((y0 + y1) / 2 - h / 2) / foto.altura) * 100,
            largura: (w / foto.largura) * 100,
        },
        faltaCenario: true,
        semPessoas: false,
    };
}
// ─── O pedido ao servidor ─────────────────────────────────────────────────────
/**
 * Os dois botões de IA recebem a foto e o espaço onde ela vai. A célula vem em
 * qualquer unidade (mm): só a proporção entra nas contas.
 */
export const pedidoDeEnquadramentoIaSchema = z.object({
    geracaoId: z.number().int().positive(),
    /** A versão da foto em que a IA trabalha; sem ela, a peça publicada. */
    fonte: z.enum(FONTES_DA_FOTO).optional(),
    celula: z.object({
        largura: z.number().finite().positive().max(100_000),
        altura: z.number().finite().positive().max(100_000),
    }),
});
//# sourceMappingURL=enquadramento-ia.js.map